import type { Block, Fragment, Inline, Text } from '@gitbook/slate';
import { deepEquals, type Map as IMap } from '@gitbook/util-immutable';

/**
 * Deep comparison of any slate nodes.
 *
 * Follows the compareFn API like Array.sort
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#description
 */
export function compareSlateNodes(
    a: Block | Text | Inline | Fragment,
    b: Block | Text | Inline | Fragment
): number {
    if (a.key !== b.key) {
        return 1;
    }

    return compareSlateNodesIgnoringKeys(a, b);
}

/**
 * Deep comparison of any slate nodes. It does not compare keys.
 *
 * Follows the compareFn API like Array.sort
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#description
 */
export function compareSlateNodesIgnoringKeys(
    a: Block | Text | Inline | Fragment,
    b: Block | Text | Inline | Fragment
): number {
    if (a.object !== b.object) {
        return 1;
    }

    if (a.object === 'text' || b.object === 'text') {
        if (a.object !== 'text' || b.object !== 'text') {
            return 1;
        }
        return compareTextNodes(a, b);
    }

    if (a.type !== b.type || !isDataEqual(a.data, b.data)) {
        return 1;
    }

    if (a.object === 'inline' || a.object === 'block' || a.object === 'fragment') {
        // defaulting to true for inline & fragments objects because they don't have nested fragments
        let areFragmentsEqual = !(a.object === 'block' && b.object === 'block');
        if (a.object === 'block' && b.object === 'block') {
            areFragmentsEqual = a.fragments.every((aFragment) => {
                const bFragment = b.fragments.get(aFragment.fragmentKey);
                return (
                    bFragment !== undefined &&
                    compareSlateNodesIgnoringKeys(aFragment, bFragment) === 0
                );
            });
        }
        return (
            // Same number of nodes
            a.nodes.size === b.nodes.size &&
                // All nodes equal
                a.nodes.every(
                    (aChild, index: number) =>
                        // @ts-expect-error
                        compareSlateNodesIgnoringKeys(aChild, b.nodes.get(index)) === 0
                ) &&
                areFragmentsEqual
                ? 0
                : 1
        );
    }

    return 0;
}

/**
 * Compare two text nodes
 */
function compareTextNodes(a: Text, b: Text): number {
    if (a === b) {
        return 0;
    }
    if (!a || !b) {
        return 1;
    }

    const aLeaves = a.getLeaves();
    const bLeaves = b.getLeaves();
    return aLeaves.size === bLeaves.size &&
        aLeaves.every((aLeaf, index) => {
            const bLeaf = bLeaves.get(index);
            return aLeaf.text === bLeaf?.text && aLeaf.marks.equals(bLeaf?.marks);
        })
        ? 0
        : 1;
}

/**
 * Compare two set of node datas.
 */
export function isDataEqual(a: IMap<string, any> | void, b: IMap<string, any> | void): boolean {
    if (a === b) {
        return true;
    }
    if (!a || !b) {
        return false;
    }

    return deepEquals(a, b, {
        // When comparing data, we ignore undefined values in keyed collections/objects,
        // because they are equivalent to missing keys.
        ignoreKeyedUndefined: true,
    });
}
